namespace Solovis.UI {

  // FileUploadControl is a control that allows for async file uploading via ajax.
  /*
   * Options:
   *  url: controller method to which the file will be POSTed
   *  accept: file extensions to allow.  example: ".xlsx, .csv"
   *  allowMultipleFiles: if true, multiple files can be uploaded at same time
   *  maxSize: max filesize to allow (in bytes)
   *  beforeSend: beforeSend handler
   *  success: successful upload handler
   *  error: error handler
   *  uploadComplete: callback after file upload (regardless of whether file upload succeeded or failed)
   *  customFormDataCallback: function(formData) which can add additional data to the upload form.
   */
  export class FileUploadControl extends Solovis.UI.BaseControl {

    public _fileDrag: boolean;
    public _url: string;
    public _accept: string;
    public _allowMultipleFiles: boolean;
    public _maxSize;
    public _maxTotalSize;
    public _beforeSend;
    public _success;
    public _error;
    public _uploadComplete;
    public _customFormDataCallback;

    public uploadResult;
    public _files: FileList | Array<File>;

    public _isDropHereLabelVisible;
    public _isDropHereLabelActive;

    public _form;
    public _selectedFiles;
    public _browseButton;

    public _formContainer: JQuery;
    public _dropHereLabel: JQuery;
    public _dropHereLabelTextSpan: JQuery;

    public _progressHeight;
    public _panel: JQuery;
    public _uploadContainer: JQuery;
    public _fileInput: JQuery;
    public _uploadButton: JQuery;
    public _progress: JQuery;
    public _progressControlHost: Solovis.UI.ControlHost;
    public _progressControl: Solovis.UI.ProgressControl;

    public _documentDragOverEventHandler;
    public _documentDragEventHandler;
    public _windowMouseMoveHandler;

    public _lastDragEventTime;


    constructor(options: {
      url?: string,
      accept?: string,
      allowMultipleFiles?: boolean,
      maxSize?: number,
      maxTotalSize?: number,
      beforeSend?: () => void,
      success?: () => void,
      error?: () => void,
      uploadComplete?: () => void,
      customFormDataCallback?: (any) => void,
    } & BaseControlOptions) {
      super(options);

      var self = this;
      if ((window as any).File && (window as any).FileList && (window as any).FileReader) {
        this._fileDrag = true;
      }
      this._url = Solovis.resolveUrl(options.url);
      this._accept = options.accept ? options.accept : null;
      this._allowMultipleFiles = options.allowMultipleFiles ? true : false;
      this._maxSize = options.maxSize;
      this._maxTotalSize = options.maxTotalSize;
      this._beforeSend = options.beforeSend;
      this._success = options.success;
      this._error = options.error;
      this._uploadComplete = options.uploadComplete;
      this._customFormDataCallback = options.customFormDataCallback;

      this.uploadResult = null;
      this._files = [];

      // We will be modifying the drop here label based on mouse events,
      //   so we are tracking visibility and active state with booleans so we're not inspecting the DOM too often.
      this._isDropHereLabelVisible = false;
      this._isDropHereLabelActive = false;
    }


    public reset() {
      if (this._files && this._files.length > 0) {
        this.uploadResult = null;
        this._files = [];
        this._form[0].reset();
        this._selectedFiles.text("No files selected.");
        this._setUploadBtnState();
        this._browseButton.get(0).style.opacity = '1';
      }
    }

    public validateFiles() {
      if (this._files && this._files.length > 0) {
        if (this._files.length > 1 && !this._allowMultipleFiles) {
          const messageHtml = "Multiple files are not allowed.";
          Solovis.UI.alert(messageHtml, "Error", null);
          this.reset();
          return false;
        }

        for (var fileIndex = 0; fileIndex < this._files.length; fileIndex++) {
          var file = this._files[fileIndex];
          var name = file.name;

          // Verify maximum size.
          if (this._maxSize && file.size > this._maxSize) {
            const mb = this._maxSize / 1024 / 1024;
            const messageHtml = "Maximum file size is " + mb.toFixed(1) + "mb.";
            Solovis.UI.alert(messageHtml, "Error: File Too Large", null);
            this.reset();
            return false;
          }

          // Verify accepted file type.
          if (Solovis.isNotNull(this._accept)) {
            var accepts = this._accept.split(",");
            var extension = name.substr(name.lastIndexOf('.'));

            var isValid = false;

            for (var i = 0; i < accepts.length; i++) {
              var accepted = accepts[i];
              accepted = accepted ? accepted.trim() : null;
              if (accepted === extension) {
                isValid = true;
              }
            }

            if (!isValid) {
              const messageHtml = "Allowed file extensions are " + this._accept;
              Solovis.UI.alert(messageHtml, "Error: Invalid file type", null);
              this.reset();
              return false;
            }
          }
        }

      }

      const totalFileSize = (Array as any).from(this._files).reduce((acc, file) => acc + file.size, 0);
      if (this._maxTotalSize && totalFileSize > this._maxTotalSize) {
        const mb = this._maxTotalSize / 1024 / 1024;
        const messageHtml = "Maximum total file size is " + mb.toFixed(1) + "mb.";
        Solovis.UI.alert(messageHtml, "Error: Files Too Large", null);
        this.reset();
        return false;
      }

      return true;
    }

    public doRender() {
      var self = this;

      // Allow the "Drop here to upload" message to be positioned absolutely within the control by using position: relative at top level.
      this._formContainer = $("<div>")
        .css("position", "relative")
        .appendTo(this.container);

      this._createUploadSelectionForm();

      this._dropHereLabel = $("<div>")
        .addClass("dnd-label hidden")
        .appendTo(this._formContainer);

      this._dropHereLabelTextSpan = $("<span>").text("Drop here to upload").appendTo(this._dropHereLabel);

      // Register for file drag and drop events.
      if (this._fileDrag) {
        this._documentDragOverEventHandler = function (e) { self._handleDocumentDragOver(e); };

        // We register for dragover at top-level document level, so we can prevent the file from opening in browser 
        //   if someone misses the drop target.
        document.addEventListener('dragover', this._documentDragOverEventHandler, false);

        var dropZone = this._formContainer.get(0);
        dropZone.addEventListener('dragover', function (e) { self._handleDrag(e); }, false);
        dropZone.addEventListener('dragleave', function (e) { self._handleDrag(e); }, false);
        dropZone.addEventListener('drop', function (e) { self._handleDrop(e); }, false);
      }

      this._setUploadBtnState();
    }

    public _createUploadSelectionForm() {
      var self = this;
      this._progressHeight = "10px";

      this._form = $("<form>")
        .attr("enctype", "multipart/form-data")
        .css("padding-bottom", this._progressHeight)
        .appendTo(this._formContainer);

      this._panel = $("<div>")
        .addClass("panel panel-default")
        .css("width", "360px")
        .css("margin-bottom", "0")
        .appendTo(this._form);

      var heading = $("<div>")
        .addClass("panel-heading")
        .appendTo(this._panel);

      var title = $("<h3>")
        .addClass("panel-title")
        .text("File Upload")
        .appendTo(heading);

      if (this._accept) {
        $("<small>").text(" (" + this._accept + ")").appendTo(title);
      }

      var body = $("<div>").addClass("panel-body").appendTo(this._panel);

      this._uploadContainer = $("<div>")
        .addClass("file-upload-container")
        .addClass("pull-left")
        .appendTo(body);

      this._selectedFiles = $("<div>")
        .addClass("pull-left")
        .attr("style", "padding-top: 5px; text-overflow: ellipsis; overflow: hidden; max-width: 230px; white-space: nowrap")
        .text("No files selected.")
        .appendTo(this._uploadContainer);

      this._fileInput = $("<input>")
        .attr("name", "fileInput")
        .attr("type", "file")
        .attr("accept", this._accept)
        .change(function () {
          self._fileInputChange();
        });

      if (this._allowMultipleFiles) {
        this._fileInput.attr("multiple", "multiple");
      }

      this._browseButton = $("<span>")
        .addClass("btn btn-default btn-file btn-sm pull-right margin-0")
        .attr("title", "Browse for file")
        .append(
          $("<span>")
            .addClass("glyphicon glyphicon-folder-open")
        )
        .append(this._fileInput)
        .appendTo(this._uploadContainer);

      if (this._url) {
        this._uploadButton = $("<button>")
          .attr("type", "button")
          .addClass("btn btn-primary btn-sm pull-right")
          .attr("style", "margin:0; height:34px")
          .text("Upload")
          .click(function () {
            self._doUpload();
          })
          .appendTo(body);
      } else {
        this._uploadContainer.css("width", "100%");
      }

      // Progress indicator.
      this._progress = $("<div>")
        .addClass("hidden")
        .css("clear", "both")
        .css("padding-top", "5px")
        .appendTo(body);
      this._progressControlHost = new Solovis.UI.ControlHost({ container: this._progress });
      this._progressControl = new Solovis.UI.ProgressControl({
        controlHost: this._progressControlHost,
        height: this._progressHeight
      });
      this._progressControlHost.setCurrentControl(this._progressControl);
    }

    public doDestroy() {
      if (this._documentDragEventHandler) {
        document.removeEventListener('dragover', this._documentDragEventHandler, false);
        this._documentDragEventHandler = null;
      }
    }

    public _stopEvent(event) {
      event.cancelBubble = true;
      event.returnValue = false;
      event.stopPropagation && event.stopPropagation();
      event.preventDefault && event.preventDefault();
      return event;
    }

    public _handleDrop(e: DragEvent) {
      this._stopEvent(e);

      this._files = e.dataTransfer.files;
      this._hideDropHereLabel();
      if (this.validateFiles()) {
        var formData = new FormData();
        // Note: You MUST append the files themselves (not the e.dataTransfer.files array itself).
        const files = e.dataTransfer.files;
        for (let i = 0; i < files.length; i++) {
          formData.append("fileInput[" + i + "]", files[i]);
        }

        if (this._url) {
          this._doUpload(formData);
        } else {
          this._fileInputChange(e);
        }
      }
    }

    public _showDropHereLabel(active) {
      var self = this;

      if (!this._isDropHereLabelVisible) {
        this._isDropHereLabelVisible = true;

        // We set up these heights so that the drag and drop label will be able to vertically center its text. 
        var panelHeight = this._panel.outerHeight();
        this._dropHereLabel.outerHeight(panelHeight);
        this._dropHereLabel.outerWidth(this._panel.outerWidth());
        this._dropHereLabelTextSpan.css("line-height", (panelHeight - 5) + "px");

        this._dropHereLabel.removeClass("hidden");

        this._windowMouseMoveHandler = function () {
          var millisecondsSinceLastDrag = (new Date() as any) - self._lastDragEventTime;
          if (millisecondsSinceLastDrag > 50) {
            // Handle case where the drag operation has stopped but we have no way of knowing directly.
            self._hideDropHereLabel();
          }
        };

        $(window).bind("mousemove", this._windowMouseMoveHandler);
      }

      if (active && !this._isDropHereLabelActive) {
        this._isDropHereLabelActive = true;
        this._dropHereLabel.addClass("active");
      }
      else if (!active && this._isDropHereLabelActive) {
        this._isDropHereLabelActive = false;
        this._dropHereLabel.removeClass("active");
      }
    }

    public _hideDropHereLabel() {
      if (this._isDropHereLabelVisible) {
        this._isDropHereLabelVisible = false;

        this._dropHereLabel.addClass("hidden");
      }

      if (this._windowMouseMoveHandler) {
        $(window).unbind("mousemove", this._windowMouseMoveHandler);
        this._windowMouseMoveHandler = null;
      }
    }

    public _isMouseEventOverPanel(e) {
      // Check if the mouseEvent coordinates are inside of the _panel rectangle.
      var panelOffset = this._panel.offset();
      var panelWidth = this._panel.outerWidth();
      var panelHeight = this._panel.outerHeight();

      var rect = this._panel.get(0).getBoundingClientRect();
      if ((e.pageX < panelOffset.left + panelWidth)
        && (e.pageX >= panelOffset.left)
        && (e.pageY < panelOffset.top + panelHeight)
        && (e.pageY >= panelOffset.top)) {

        return true;
      }

      return false;
    }

    public _handleDocumentDragOver(e) {
      this._stopEvent(e);
      this._lastDragEventTime = new Date();

      // We prevent dropping on the document, so that the the browser will not try to load a file that is dropped there.
      // NOTE: If we ever have more than one drag and drop control on a page, we'll probably need our own coordinator so that every
      //   control has a fair chance at seeing the events.
      e.dataTransfer.dropEffect = 'none';

      this._showDropHereLabel(this._isMouseEventOverPanel(e));
    }

    public _handleDrag(e) {
      var self = this;

      this._stopEvent(e);
      this._lastDragEventTime = new Date();

      if (e.type == "dragover") {
        if (this._isMouseEventOverPanel(e)) {
          this._showDropHereLabel(true);
          e.dataTransfer.dropEffect = 'copy';
        }
        else {
          this._showDropHereLabel(false);
          e.dataTransfer.dropEffect = 'none';
        }
      }
      else {
        if (e.type == "dragleave") {
          this._showDropHereLabel(this._isMouseEventOverPanel(e));
        }
        else {
          this._showDropHereLabel(false);
        }
      }
    }

    public _setUploadBtnState() {
      if (this._uploadButton) {
        if (this._files) {
          if (this._files.length > 0) {
            this._uploadButton.prop('disabled', false);
          }
          else {
            this._uploadButton.prop('disabled', true);
          }
        }
        else {
          this._uploadButton.prop('disabled', true);
        }
      }
    }

    public _fileInputChange(e?) {
      if (e) {
        this._files = e.dataTransfer.files;
      } else {
        this._files = (this._fileInput[0] as any).files;
      }

      if (this.validateFiles()) {
        this._selectedFiles.text((Array as any).from(this._files).map(file => file.name).join(", "));
        this._setUploadBtnState();
      }
    };

    public _doUpload(formData?) {
      var self = this;
      if (!formData) {
        formData = new FormData(this._form[0]);
      }

      if ((Solovis as any).Info.csrfToken) {
        formData.append("X-SOLOVIS-CSRF", (Solovis as any).Info.csrfToken);
      }

      if (this._customFormDataCallback) {
        this._customFormDataCallback(formData);
      }

      $.ajax({
        url: this._url,
        type: 'POST',
        xhr: function () {
          var myXhr = $.ajaxSettings.xhr();
          if (myXhr.upload) {
            // Monitor progress of the upload.
            myXhr.upload.addEventListener('progress', function () {
              self._handleProgress(this);
            }, false);
          }
          return myXhr;
        },
        // Ajax events
        beforeSend: function () {
          self._handleBeforeSend();
        },
        success: function (data, textStatus, XMLHttpRequest) {
          if (Solovis.Rpc.shouldPollForResult(data, self._url, XMLHttpRequest)) {
            Solovis.Rpc.pollForJobResult(data.result.jobId, self._processResult, self);
          } else {
            self._processResult.call(self, data);
          }
          self._processResult.call(self, data);
        },
        error: function (e) {
          const cType = e.getResponseHeader("Content-Type");
          const zScalerBlocked = e.status === 403 && cType === "text/html" && e.responseText.indexOf("Zscaler") !== -1;
          var result = {
            error: {
              message: zScalerBlocked ? "Zscaler blocked your request. This may be because your file is password protected." : "Request failed.",
              http: {
                status: e.status,
                responseText: e.statusText,
                contentType: cType,
              },
            }
          };
          self._processResult.call(self, result);
        },
        // Form data
        data: formData,
        // Options to tell jQuery not to process data or worry about content-type.
        cache: false,
        contentType: false,
        processData: false
      });
    }

    public _processResult(data) {
      this._hideProgress();

      this.uploadResult = data;

      if (data.error) {
        if (this._error) {
          this._error();
        }
      }
      else if (this._success) {
        this._success();
      }

      if (this._uploadComplete) {
        this._uploadComplete();
      }

      $(this).trigger("uploadComplete");
    }

    public _handleBeforeSend() {
      this._showProgress();

      if (this._beforeSend) {
        this._beforeSend();
      }
    }

    public _showProgress() {
      this._progress.removeClass("hidden");
      this._form.css("padding-bottom", "0");
    }

    public _hideProgress() {
      this._progress.addClass("hidden");
      this._form.css("padding-bottom", this._progressHeight);
    }

    public _handleProgress(e) {
      if (e.lengthComputable) {
        this._progress.attr({
          value: e.loaded, max: e.total
        });
      }
    }
  }

}
